import { CACHE_CONFIG } from '@/common/constants'
import { StatsCard } from '@/components/StatsCard/StatsCard'
import { useDealership } from '@/contexts/DealershipContext'
import { getConfiguration } from '@/services/configuration.api'
import { Lead } from '@/services/conversations.api'
import {
  DEALERSHIP_APPOINTMENTS_PAGE_SIZE,
  getDealershipDashboardStats,
  listDealershipAppointments,
  type DealershipAppointmentListItem,
  type DealershipAppointmentsTabFilter,
} from '@/services/dealership.api'
import { BusinessBasicsConfig } from '@/types/configuration'
import { formatGroupStatsRangeLabel, getIntervalIsoRange, type GroupInterval } from '@/utils/groupDashboardInterval'
import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Loader,
  Menu,
  Pagination,
  ScrollArea,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
  Tooltip,
  useComputedColorScheme,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconCalendar,
  IconCar,
  IconCheck,
  IconClock,
  IconEdit,
  IconEye,
  IconFlag,
  IconMenu2,
  IconRobot,
  IconSearch,
  IconTrash,
  IconUser,
  IconX,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RecoveryStatusCard } from '../Activities/components/RecoveryStatusCard'

function mapAppointmentRowToLead(row: DealershipAppointmentListItem): Lead {
  return {
    id: row.appointmentId,
    customerName: row.customerName,
    customerPhone: row.customerPhone ?? undefined,
    customerEmail: row.customerEmail ?? undefined,
    leadSource: row.leadSource ?? undefined,
    vehicle: row.vehicle ?? undefined,
    appointmentTime: row.scheduledAt,
    appointmentStatus: row.status as Lead['appointmentStatus'],
    totalMessages: row.messageCount,
    isSold: row.isSold,
    conversationId: row.conversationId ?? undefined,
    customerId: row.customerId,
    createdAt: row.scheduledAt,
    appointmentSource: row.source ?? null,
  }
}

export function AppointmentsDashboard() {
  const computedColorScheme = useComputedColorScheme('light')
  const { selectedEntity, groupDashboardInterval, setGroupDashboardInterval } = useDealership()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [appointmentsPage, setAppointmentsPage] = useState(1)
  const [activeTab, setActiveTab] = useState<string | null>('all')
  const [dealershipTimezone, setDealershipTimezone] = useState<string>('America/Chicago')

  const dealershipId = selectedEntity?.type === 'dealership' ? selectedEntity.id : ''
  const { startDate, endDate } = useMemo(() => getIntervalIsoRange(groupDashboardInterval), [groupDashboardInterval])

  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['dealership-stats', dealershipId, groupDashboardInterval],
    queryFn: () => getDealershipDashboardStats(dealershipId, startDate, endDate),
    staleTime: CACHE_CONFIG.STALE_TIME.STATS,
    gcTime: CACHE_CONFIG.GC_TIME.DEFAULT,
    retry: CACHE_CONFIG.RETRY_COUNT,
    refetchOnWindowFocus: false,
    enabled: !!dealershipId,
  })

  const rangeLabel = dashboardStats?.dateRange
    ? formatGroupStatsRangeLabel(dashboardStats.dateRange.startDate, dashboardStats.dateRange.endDate)
    : formatGroupStatsRangeLabel(startDate, endDate)

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300)
    return () => window.clearTimeout(t)
  }, [searchQuery])

  useEffect(() => {
    setAppointmentsPage(1)
  }, [dealershipId, groupDashboardInterval, activeTab, debouncedSearch])

  useEffect(() => {
    if (!selectedEntity || selectedEntity.type !== 'dealership') return
    const dealership = selectedEntity as { id: string; type: 'dealership' }
    void getConfiguration<BusinessBasicsConfig>(dealership.id, 'business_basics').then(timezoneConfig => {
      if (timezoneConfig.success && timezoneConfig.data?.timezone) {
        setDealershipTimezone(timezoneConfig.data.timezone)
      }
    })
  }, [selectedEntity])

  const tabFilter: DealershipAppointmentsTabFilter =
    activeTab === 'sold' || activeTab === 'completed' || activeTab === 'missed' ? activeTab : 'all'

  const {
    data: appointmentListPage,
    isLoading: appointmentsLoading,
    isFetching: appointmentsFetching,
  } = useQuery({
    queryKey: ['dealership-appointments', dealershipId, startDate, endDate, appointmentsPage, tabFilter, debouncedSearch],
    queryFn: () =>
      listDealershipAppointments(dealershipId, startDate, endDate, {
        page: appointmentsPage,
        pageSize: DEALERSHIP_APPOINTMENTS_PAGE_SIZE,
        tab: tabFilter,
        search: debouncedSearch || undefined,
      }),
    staleTime: CACHE_CONFIG.STALE_TIME.STATS,
    gcTime: CACHE_CONFIG.GC_TIME.DEFAULT,
    retry: CACHE_CONFIG.RETRY_COUNT,
    refetchOnWindowFocus: false,
    enabled: !!dealershipId,
  })

  useEffect(() => {
    if (appointmentListPage && appointmentListPage.page !== appointmentsPage) {
      setAppointmentsPage(appointmentListPage.page)
    }
  }, [appointmentListPage, appointmentsPage])

  const leads: Lead[] = useMemo(() => (appointmentListPage?.items ?? []).map(mapAppointmentRowToLead), [appointmentListPage])

  const appointmentsTotal = appointmentListPage?.total ?? 0
  const appointmentsTotalPages = appointmentListPage?.totalPages ?? 0

  if (!selectedEntity || selectedEntity.type !== 'dealership') {
    return <Text>Please select a dealership</Text>
  }

  // Helper to safely extract string from potential objects
  const safeString = (value: any, fallback: string = ''): string => {
    if (typeof value === 'string') return value
    if (value && typeof value === 'object' && 'description' in value) {
      return value.description || fallback
    }
    return fallback
  }

  const getSourceDisplay = (source: Lead['appointmentSource']) => {
    if (source === 'AI_ASSISTANT') {
      return (
        <Badge size="md" variant="light" color="violet" leftSection={<IconRobot size={14} />}>
          AI
        </Badge>
      )
    }
    if (source === 'CRM_SYNC') {
      return (
        <Badge size="md" variant="light" color="gray">
          CRM
        </Badge>
      )
    }
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    )
  }

  const getOutcomeBadge = (status?: Lead['appointmentStatus'], isSold?: boolean) => {
    if (!status) {
      return (
        <Badge size="md" color="gray" leftSection={<IconClock size={14} />}>
          No Appointment
        </Badge>
      )
    }

    const config: Record<string, { color: string; label: string; icon: typeof IconCheck }> = {
      // Showroom visit statuses
      COMPLETED: { color: 'teal', label: 'Visit Completed', icon: IconCheck },
      SHOWN_UNSOLD: { color: 'orange', label: 'Shown - Unsold', icon: IconCheck },
      UNSOLD_RECOVERY_PENDING: { color: 'yellow', label: 'Unsold - Pending', icon: IconClock },
      UNSOLD_RECOVERY_IN_PROGRESS: { color: 'yellow', label: 'Unsold - In Recovery', icon: IconClock },
      // Missed/No-show statuses
      NO_SHOW: { color: 'red', label: 'No Show', icon: IconX },
      MISSED_RECOVERY_PENDING: { color: 'red', label: 'Missed - Pending', icon: IconClock },
      MISSED_RECOVERY_IN_PROGRESS: { color: 'red', label: 'Missed - In Recovery', icon: IconClock },
      // Scheduled statuses
      CONFIRMED: { color: 'green', label: 'Confirmed', icon: IconCheck },
      SCHEDULED: { color: 'cyan', label: 'Scheduled', icon: IconClock },
      IN_PROGRESS: { color: 'cyan', label: 'In Progress', icon: IconClock },
      RESCHEDULED: { color: 'blue', label: 'Rescheduled', icon: IconClock },
      // Other statuses
      CANCELLED: { color: 'gray', label: 'Cancelled', icon: IconX },
      ABANDONED: { color: 'gray', label: 'Abandoned', icon: IconX },
    }

    const statusConfig = config[status] || config.SCHEDULED
    const Icon = statusConfig.icon

    return (
      <Group gap="xs">
        <Badge
          size="md"
          color={statusConfig.color}
          variant="filled"
          leftSection={<Icon size={14} />}
          styles={{
            root: {
              color: 'white',
            },
          }}
        >
          {statusConfig.label}
        </Badge>
        {isSold && (
          <Badge size="md" color="green" variant="filled" leftSection={<IconCar size={14} />}>
            Sold
          </Badge>
        )}
      </Group>
    )
  }

  const formatTime = (dateString: string) => {
    try {
      // Parse the UTC date string and format it in the dealership's timezone (time + zone abbreviation)
      return moment.utc(dateString).tz(dealershipTimezone).format('h:mm A z')
    } catch (error) {
      console.error('Failed to format time:', error)
      // Fallback to original formatting
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })
    }
  }

  const formatDate = (dateString: string) => {
    try {
      // Parse the UTC date string and format it in the dealership's timezone
      return moment.utc(dateString).tz(dealershipTimezone).format('MMM D, YYYY')
    } catch (error) {
      console.error('Failed to format date:', error)
      // Fallback to original formatting
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Group gap="xs" align="center">
            <IconCalendar size={22} stroke={1.5} />
            <Title order={2}>Leads & Appointments</Title>
          </Group>
          <Text size="sm" c="dimmed">
            Monitor leads, appointments, and showroom outcomes
          </Text>
          <Text size="xs" c="dimmed" mt={2}>
            Metrics for {rangeLabel}
            {statsLoading ? ' (loading…)' : ''}
          </Text>
        </div>
        <SegmentedControl
          value={groupDashboardInterval}
          onChange={v => setGroupDashboardInterval(v as GroupInterval)}
          data={[
            { label: 'This Week', value: 'this_week' },
            { label: 'This Month', value: 'this_month' },
            { label: 'Last Month', value: 'last_month' },
            { label: 'All Time', value: 'all_time' },
          ]}
        />
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatsCard
          title="Leads"
          icon={IconUser}
          color="violet"
          metricKey="totalLeads"
          dualMetric={
            dashboardStats
              ? {
                  total: dashboardStats.totalLeads,
                  ai: dashboardStats.conversationsFirstContact,
                }
              : { total: 0, ai: 0 }
          }
        />
        <StatsCard
          title="Appointments"
          icon={IconCalendar}
          color="blue"
          metricKey="totalAppointments"
          dualMetric={
            dashboardStats
              ? {
                  total: dashboardStats.totalAppointments,
                  ai: dashboardStats.totalAppointmentsAi,
                }
              : { total: 0, ai: 0 }
          }
        />
        <StatsCard title="Showroom Visits" value={dashboardStats?.showroomVisits ?? 0} icon={IconClock} color="orange" />
        <StatsCard
          title="Vehicles Sold"
          value={dashboardStats?.vehiclesSold ?? 0}
          icon={IconCar}
          color="green"
          metricKey="vehiclesSold"
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1 }} spacing="md">
        <StatsCard
          title="Conversations Stopped"
          icon={IconFlag}
          color="orange"
          tripleMetric={
            dashboardStats
              ? {
                  left: dashboardStats.conversationsCrmStopped,
                  middle: dashboardStats.conversationsAbandoned,
                  right: dashboardStats.conversationsEscalated,
                }
              : { left: 0, middle: 0, right: 0 }
          }
          tripleMetricLabels={{
            left: 'CRM',
            middle: 'Abandoned',
            right: 'AI',
          }}
          tripleMetricRightAiVisual
        />
      </SimpleGrid>

      {/* Recovery Status */}
      <RecoveryStatusCard />

      {/* Search and Tabs */}
      <Box>
        <Group mb="md" align="center" justify="space-between" wrap="wrap" gap="sm">
          <TextInput
            placeholder="Search leads..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            w={{ base: '100%', xs: 260 }}
            maw={320}
          />
          <Group gap="sm" wrap="wrap" align="center" justify="flex-end" style={{ flex: '1 1 200px' }}>
            <Text size="sm" c="dimmed" style={{ lineHeight: 1.45 }} component="span">
              {appointmentsTotal === 0
                ? 'No appointments in this view'
                : `Showing ${leads.length ? (appointmentsPage - 1) * DEALERSHIP_APPOINTMENTS_PAGE_SIZE + 1 : 0}-${(appointmentsPage - 1) * DEALERSHIP_APPOINTMENTS_PAGE_SIZE + leads.length} of ${appointmentsTotal}`}
              {appointmentsFetching ? ' · Updating…' : ''}
            </Text>
            {appointmentsTotalPages > 1 && (
              <Pagination total={appointmentsTotalPages} value={appointmentsPage} onChange={setAppointmentsPage} size="sm" />
            )}
          </Group>
        </Group>

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="all">All Appointments</Tabs.Tab>
            <Tabs.Tab value="completed">Completed</Tabs.Tab>
            <Tabs.Tab value="missed">Missed / No Show</Tabs.Tab>
            <Tabs.Tab value="sold">Sold</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </Box>

      {/* Table */}
      <Box style={{ position: 'relative' }}>
        {appointmentsLoading && (
          <Box style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
            <Loader size="lg" />
          </Box>
        )}

        <ScrollArea type="auto">
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead
              style={{
                backgroundColor: computedColorScheme === 'dark' ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-1)',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <Table.Tr>
                <Table.Th style={{ fontWeight: 600, fontSize: '14px' }} c={computedColorScheme === 'dark' ? 'gray.0' : undefined}>
                  Lead Details
                </Table.Th>
                <Table.Th style={{ fontWeight: 600, fontSize: '14px' }} c={computedColorScheme === 'dark' ? 'gray.0' : undefined}>
                  Vehicle of Interest
                </Table.Th>
                <Table.Th style={{ fontWeight: 600, fontSize: '14px' }} c={computedColorScheme === 'dark' ? 'gray.0' : undefined}>
                  Appointment
                </Table.Th>
                <Table.Th style={{ fontWeight: 600, fontSize: '14px' }} c={computedColorScheme === 'dark' ? 'gray.0' : undefined}>
                  Outcome
                </Table.Th>
                <Table.Th style={{ fontWeight: 600, fontSize: '14px' }} c={computedColorScheme === 'dark' ? 'gray.0' : undefined}>
                  Source
                </Table.Th>
                <Table.Th
                  style={{ width: '100px', fontWeight: 600, fontSize: '14px' }}
                  c={computedColorScheme === 'dark' ? 'gray.0' : undefined}
                >
                  Actions
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {leads.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c="dimmed" ta="center" py="xl">
                      No leads found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                leads.map(lead => (
                  <Table.Tr key={lead.id}>
                    {/* Lead Details */}
                    <Table.Td>
                      <div>
                        <Text size="sm" fw={600}>
                          {lead.customerName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {lead.customerPhone || lead.customerEmail}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {safeString(lead.leadSource, 'Website Form')}
                        </Text>
                      </div>
                    </Table.Td>

                    {/* Vehicle */}
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {safeString(lead.vehicle, '-')}
                      </Text>
                    </Table.Td>

                    {/* Appointment */}
                    <Table.Td>
                      {lead.appointmentTime ? (
                        <Group gap="xs">
                          <IconCalendar size={14} />
                          <div>
                            <Text size="sm" fw={500}>
                              {formatDate(lead.appointmentTime)}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {formatTime(lead.appointmentTime)}
                            </Text>
                          </div>
                        </Group>
                      ) : (
                        <Text size="sm" c="dimmed">
                          No appointment
                        </Text>
                      )}
                    </Table.Td>

                    {/* Outcome */}
                    <Table.Td>{getOutcomeBadge(lead.appointmentStatus, lead.isSold)}</Table.Td>

                    {/* Source */}
                    <Table.Td>{getSourceDisplay(lead.appointmentSource)}</Table.Td>

                    {/* Actions */}
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Tooltip label={lead.conversationId ? 'View Conversation' : 'No conversation available'}>
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            disabled={!lead.conversationId}
                            onClick={() => {
                              if (!lead.conversationId) return
                              navigate('/dealership/conversations', {
                                state: {
                                  conversationId: lead.conversationId,
                                  customerId: lead.customerId,
                                  customerName: lead.customerName,
                                  customerPhone: lead.customerPhone,
                                  customerEmail: lead.customerEmail,
                                },
                              })
                            }}
                          >
                            <IconEye size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Menu shadow="md" width={200}>
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray">
                              <IconMenu2 size={18} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Label>Lead Actions</Menu.Label>
                            <Menu.Item
                              leftSection={<IconEdit size={14} />}
                              onClick={() => {
                                notifications.show({
                                  message: 'Edit functionality coming soon',
                                  color: 'blue',
                                })
                              }}
                            >
                              Edit Details
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconCalendar size={14} />}
                              onClick={() => {
                                notifications.show({
                                  message: 'Reschedule functionality coming soon',
                                  color: 'blue',
                                })
                              }}
                            >
                              Reschedule
                            </Menu.Item>
                            <Menu.Divider />
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() => {
                                notifications.show({
                                  message: 'Delete functionality coming soon',
                                  color: 'blue',
                                })
                              }}
                            >
                              Delete Lead
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Box>
    </Stack>
  )
}
